import type {
  FactCatalogueBinding,
  WorkflowFactSchema,
} from '@/features/workflows/facts'
import type { PatientRecord } from '@/features/patients/types'
import type { ClientRenewal } from '@/features/renewals/types'
import type {
  AuthMethod,
  DutyHandoffType,
  SmartTransferAssistantSettings,
  SmartTransferContextEngineering,
  SmartTransferFirstMessageMode,
  TransferDestination,
} from '@/features/tools/types'
export type {
  DutyHandoffType,
  SmartTransferAssistantSettings,
  SmartTransferContextEngineering,
  SmartTransferFirstMessageMode,
  TransferDestination,
} from '@/features/tools/types'

export type DutyStatus = 'active' | 'inactive' | 'archived'

/**
 * One row of the duty's client-assignment list: an `app_patients_duties` row
 * joined to its client. Extends `PatientRecord` (same keys as the Clients list)
 * so the patients columns and details sheet can be reused as-is.
 *
 * `id` is the CLIENT id; `patient_duty_id` keys the assignment row. `duty_status`
 * is the patient-duty status — not the client's own `is_active` flag.
 */
export interface DutyClientRecord extends PatientRecord {
  patient_duty_id: string
  duty_status: string | null
  /** Set on a renewal duty's rows only. */
  renewal?: ClientRenewal
}

/**
 * A duty's client-selection criteria, from `app_duties`:
 * `client_assignment_sql` (the query that runs) and its human-readable twin
 * `client_assignment_sql_readable`. Either can be null — most duties have
 * neither configured yet.
 */
export interface DutyAssignmentLogic {
  sql: string | null
  readable: string | null
}

/**
 * A duty's client-exclusion criteria, from `app_duties`: `client_exclusion_sql`
 * (the query that runs) and its human-readable twin
 * `client_exclusion_sql_readable`. Removes patients even when they match
 * `DutyAssignmentLogic`. Either field can be null — most duties have neither
 * configured yet.
 */
export interface DutyExclusionLogic {
  sql: string | null
  readable: string | null
}

/** What started an assignment run, as relay records it on the run. */
export const ASSIGNMENT_RUN_TRIGGERS = [
  'daily',
  'khs',
  'manual',
  'script',
] as const

/**
 * One committed run of a duty's assignment and exclusion rules, from relay's
 * `app_duty_assignment_runs`. Counts only: the members behind them stay
 * server-side for internal reporting.
 */
export interface DutyAssignmentRun {
  trigger: (typeof ASSIGNMENT_RUN_TRIGGERS)[number]
  created_at: string
  assigned_count: number
  reopened_count: number
  closed_count: number
  /** The part of `closed_count` the exclusion rule closed. */
  excluded_count: number
}

export interface DutyRecord {
  id: string
  name: string
  patient_count: number
  call_count: number
  versions_count: number
  config_updated_at: string | null
  computer_use: boolean
  // Number of Nova computer-use workflows assigned to this duty (via the
  // app_nova_workflow_duties junction). Shown in the list when nonzero.
  computer_use_workflow_count: number
  status: DutyStatus
  // Emails this duty is shared with. Empty means visible org-wide, which is
  // the default and what every duty predating sharing carries.
  access: string[]
}
export interface DutyConnectionWindow {
  start_time?: string | null
  end_time?: string | null
  concurrent_limit?: number | null
}

export interface OrgPhoneNumber {
  id: string
  number: string
  label?: string | null
}

export interface DutyConnectionInfo {
  phone_number?: string | null
  phone_number_id?: string | null
  phone_number_label?: string | null
  timezone?: string | null
  windows: Record<string, DutyConnectionWindow>
  available_phone_numbers?: OrgPhoneNumber[]
  is_operator?: boolean
  /** Resolved holiday dates ('YYYY-MM-DD') on which outbound calling is blocked. */
  holidays?: string[]
  /** Org default holiday dates, shown when the duty inherits them. */
  org_holidays?: string[]
  /** True when the duty pins its own holiday list instead of inheriting. */
  holidays_overridden?: boolean
}

export interface DutyCallVolumeMonitorNotificationConfig {
  slack_channel_id?: string | null
  fallback_emails: string[]
}

export type DutyPatientMemoryScope = 'current_duty' | 'organization'

export interface DutyPatientMemorySettings {
  scope: DutyPatientMemoryScope
  include_past_duties: boolean
  lookback_days: number
  max_memories_per_duty: number
  custom_instructions: string | null
}

export interface DutyPatientMemoryConfig extends DutyPatientMemorySettings {
  test_config: DutyPatientMemorySettings | null
}

export interface DutyPatientMemoryInstruction {
  kind: 'context' | 'handoff_routing'
  text: string
}

export interface DutyPatientMemoryDetail {
  enabled: boolean
  config: DutyPatientMemoryConfig
  creation_model: string
  creation_prompt: string
  effective_instructions: DutyPatientMemoryInstruction[]
  test_effective_instructions: DutyPatientMemoryInstruction[]
  eligible_duties: Array<{
    id: string
    name: string
    status: 'active' | 'inactive' | 'archived'
  }>
}

export interface DutyPatientMemoryPatch {
  enabled?: boolean
  config?: DutyPatientMemoryConfig
}

export interface TestMemoryEntry {
  duty_id: string
  content: string
}

export interface DutyTestMemories {
  test_patient_id: string
  entries: TestMemoryEntry[]
}

export interface DutyCallVolumeMonitorConfig {
  mode?: 'rolling' | 'scheduled'
  scheduled_windows?: Array<{
    start_time: string
    end_time: string
    expected_calls: number
  }>
  enabled: boolean
  warning_threshold_pct: number
  critical_threshold_pct: number
  evaluation_window_minutes: 15 | 30 | 45 | 60 | 180 | 1440
  warmup_grace_minutes: number
  expected_calls: number
  notifications: DutyCallVolumeMonitorNotificationConfig
  auto_resolve: boolean
  auto_resolve_consecutive_ok: number
  critical_reminder_minutes?: number | null
}

export interface DutyCallVolumeMonitorState {
  state: 'healthy' | 'warning' | 'critical'
  metrics: {
    sent_calls?: number
    expected_calls?: number
    ratio_pct?: number
    evaluation_window_minutes?: number
  }
  open_alert_started_at?: string | null
  last_transition_at?: string | null
  last_notified_at?: string | null
  consecutive_healthy_windows?: number
}

export interface DutyCallVolumeMonitor {
  duty_id: string
  monitor_type: 'call_volume'
  config: DutyCallVolumeMonitorConfig
  state: DutyCallVolumeMonitorState
}

export type DutyToolKey =
  | 'mercury:booking'
  | 'complain'
  | 'voicemail'
  | 'transfer_call'
  | 'labels'
  | 'facts_extraction'
  | 'resequence'
  | 'recurring'
  | 'handoff_to_duty'
  | 'authenticate_person'
  | 'post_call_messages'
  | 'fast_first_message'
  | 'patient_memory'
  // Encounter Note. Names the Mercury capability, but unlike the keys above it
  // is not stored in app_duties.tools — its on/off state is the duty's Nova
  // workflow registration (see WORKFLOW_TOOL_SLOTS and, server-side,
  // `_patch_workflow_backed_tool`).
  | 'mercury:encounter'
  | 'mercury:patient'

export interface DutyTransferCallSettings {
  destinations: TransferDestination[]
}

export interface DutyHandoffToDutySettings {
  config: Record<string, unknown> | null
}

export interface DutyAuthenticatePersonSettings {
  numeric_confirmation?: boolean | { unrecognized: unknown }
  // Unrecognized server values are retained for read-only display, never saved.
  phone_lookup?:
    | false
    | { verify_with_any: string[] }
    | { unrecognized: unknown }
  methods: AuthMethod[]
  allowed_persons: string[]
  org_methods: AuthMethod[] | null
  org_allowed_persons: string[] | null
  methods_overridden: boolean
  persons_overridden: boolean
}

export interface DutyToolsInfo {
  tools: DutyToolKey[]
  transfer_call_settings?: DutyTransferCallSettings
  handoff_to_duty_settings?: DutyHandoffToDutySettings
  authenticate_person_settings?: DutyAuthenticatePersonSettings
  is_operator?: boolean
}

/**
 * One fact a workflow accepts, as Nebula describes it in `facts_schema`. Built
 * from the workflow's YAML with the duty's `facts_options` overlay applied, so
 * these are the facts *this duty* would pass on invoke. `env` names an
 * environment variable the runner falls back to when the fact is omitted.
 *
 * The same shape Nebula returns on the org-scoped listing, so it is an alias of
 * that type rather than a parallel declaration — both are narrowed by the one
 * `parseFactsSchema`.
 */
export type DutyWorkflowFact = WorkflowFactSchema

/**
 * A Nova workflow available to a duty, as surfaced by Nebula's duty-scoped
 * discovery endpoint (proxied through the duty `workflows` endpoint). `trigger`
 * maps a workflow to a Mercury tool category (e.g. `encounter` →
 * submit_encounter_note), which gates capability-dependent tool toggles.
 */
export interface DutyWorkflow {
  slug: string
  org_slug: string | null
  title: string | null
  trigger: string | null
  description: string | null
  /**
   * Retired in the nova repo. Nebula keeps listing it (the YAML stays on disk so
   * its historical sessions resolve) but refuses every invocation, so a duty
   * that still has one registered can look at it and turn it off — not run it.
   */
  deprecated: boolean
  /** Null when Nebula can't find/parse the workflow's YAML on disk. */
  facts_schema: Record<string, DutyWorkflowFact> | null
}

export interface DutyWorkflowsInfo {
  workflows: DutyWorkflow[]
}

/**
 * How one of a variant's facts gets its value.
 *
 * `static` pins a value; `agentic` hands the fact to the AI agent with
 * instructions; `catalogue` reads it from an entry in the org's fact catalogue.
 * These three are the whole set, but a variant may leave a fact unbound for the
 * caller or the workflow default. A `static` value may be null or the empty
 * string; both are explicit values and are distinct from leaving the fact
 * unbound.
 *
 * The catalogue arm is the workflow-level one — a fact bound by a variant and a
 * fact bound by its workflow's YAML read the same on the wire and mean the same
 * thing, so anything that renders one renders the other.
 */
export type VariantFactBinding =
  | { source: 'static'; value: string | null }
  | { source: 'agentic'; instructions: string }
  | FactCatalogueBinding

export type VariantFactSource = VariantFactBinding['source']

/**
 * A saved variant of a workflow: a named set of fact bindings the org can run.
 *
 * Variants belong to the org and its workflow, so the list is the same on every
 * duty; `enabled` is the one duty-scoped bit — whether this duty can run it,
 * which is also what decides if Mercury gets a tool for it here.
 */
export interface WorkflowVariant {
  variant_key: string
  title: string | null
  description: string | null
  active: boolean
  enabled: boolean
  /**
   * How many duties hold this variant right now — what the delete confirmation
   * uses to say how far a deletion reaches. Individual duty ids are not
   * surfaced: they could name duties the viewer isn't looking at.
   */
  duty_count: number
  /** The stored bindings, keyed by workflow fact name. */
  facts: Record<string, VariantFactBinding>
  /**
   * Stored bindings this client does not understand yet. Kept separately so an
   * older Hoku cannot erase a binding source introduced by a newer Nova.
   */
  unsupported_facts: Record<string, unknown>
  /**
   * What a caller of this variant still supplies. Static and catalogue bindings
   * drop out of the schema; agentic ones keep their slot with the instructions
   * as the description, since the calling agent is what fills them. Null when
   * Nebula can't read the workflow's YAML.
   */
  facts_schema: Record<string, DutyWorkflowFact> | null
}

export interface WorkflowVariantsInfo {
  variants: WorkflowVariant[]
}

/**
 * Thrown when saving a variant is rejected, carrying Nebula's per-fact binding
 * errors so the modal can mark the mapping row that caused each one.
 */
export class VariantSaveError extends Error {
  readonly factErrors: WorkflowFactError[]
  /**
   * The HTTP status the save was refused with, when there was one. Carried so
   * the form can tell a taken key (409) from the rest and put the message on
   * the Name field rather than in a toast that names no field.
   */
  readonly status: number | null

  constructor(
    message: string,
    factErrors: WorkflowFactError[] = [],
    status: number | null = null
  ) {
    super(message)
    this.name = 'VariantSaveError'
    this.factErrors = factErrors
    this.status = status
  }
}

/** A Nova session started from a duty's workflow. */
export interface InvokeWorkflowResult {
  session_id: string
  status: string | null
  /** True when an idempotent replay returned an existing session. */
  reused: boolean
}

/**
 * A fact-validation failure from Nebula, which keys its errors by fact name. The
 * field may name a fact the form doesn't render — a secret, say — so it isn't
 * guaranteed to match an input.
 */
export interface WorkflowFactError {
  field: string
  message: string
}

/**
 * Thrown by `patchDutyTool` when the server refused the toggle and said why.
 *
 * Distinct from a transport failure so the caller can show the server's own
 * sentence — the tools route refuses a workflow-backed tool another duty already
 * holds, naming that duty — while still falling back to a friendly line when all
 * it has is "Failed to fetch".
 */
export class DutyToolError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'DutyToolError'
  }
}

/** Thrown by `invokeDutyWorkflow` so the form can render per-fact errors. */
export class WorkflowInvokeError extends Error {
  readonly factErrors: WorkflowFactError[]

  constructor(message: string, factErrors: WorkflowFactError[] = []) {
    super(message)
    this.name = 'WorkflowInvokeError'
    this.factErrors = factErrors
  }
}

export interface DutyBookingProvider {
  id: string
  name: string
  specialty: string | null
  credentials: string | null
}

export const BOOKING_DURATION_OPTIONS = [15, 30, 45, 60] as const

export const BOOKING_TOOLS = [
  'get_timeslots',
  'book_appointment',
  'reschedule_appointment',
  'cancel_appointment',
] as const
export type BookingToolName = (typeof BOOKING_TOOLS)[number]

export interface BookingWindow {
  mode: 'fixed' | 'relative'
  start_date?: string | null
  end_date?: string | null
  earliest_days?: number | null
  latest_days?: number | null
}

export interface AppointmentTypeProvider {
  scheduling_provider_id: string
  /** EHR provider name; null sends the scheduling provider's name. */
  provider: string | null
  /** EHR resource name; null sends the scheduling provider's name. */
  resource: string | null
}

export interface AppointmentType {
  name: string
  visit_types: string[]
  duration_options_minutes: number[]
  providers: AppointmentTypeProvider[]
  booking_window: BookingWindow | null
}

export interface DutyBookingProviders {
  enabled_tools: BookingToolName[]
  facility_ids: string[]
  scheduling_instructions: string
  require_aligned_start: boolean
  write_to_ehr: boolean
  appointment_types: AppointmentType[]
  available_providers: DutyBookingProvider[]
  has_more: boolean
}

export interface DutyConversationMetrics {
  characters?: number | null
  complexity?: string | null
  script?: string | null
}

export interface DutyVoicemailMetrics {
  duration?: number | null
  characters?: number | null
  script?: string | null
}

export interface DutyLabelMappingMetrics {
  labels?: number | null
  script?: string | null
}

export type DutyFactsMap = Record<string, string | null>

export interface DutyTestPatientRowInfo {
  id?: string | null
  first_name?: string | null
  last_name?: string | null
  full_name?: string | null
  date_of_birth?: string | null
  address?: string | null
  language?: string | null
  source_id?: string | null
  patient_ids?: Record<string, unknown> | null
}

export interface DutyTestCallValidation {
  can_launch: boolean
  conflicting_keys: string[]
  errors: string[]
}

/** A source fact the duty's text reads, which a test fact may override. */
export interface DutyOverridableFact {
  fact_name: string
  value_type: string | null
  has_value: boolean
  /** The test patient's own value, before overrides. */
  value: unknown
  /** The value test_facts sets, or null when unset. */
  override: unknown
}

export interface DutyTestPatientInfo {
  phone_number?: string | null
  org_facts?: DutyFactsMap | null
  client_facts?: DutyFactsMap | null
  test_patient?: DutyTestPatientRowInfo | null
  /** Null when Relay's test-call context is unavailable. */
  overridable_facts?: DutyOverridableFact[] | null
  validation?: DutyTestCallValidation | null
  rendered_test_facts?: string | null
  parsed_test_facts?: DutyFactsMap | null
  variable_values?: Record<string, unknown> | null
}

export interface DutyFactExtractionMetrics {
  facts_as_string?: string | null
  facts_count?: number | null
}

export interface DutyResequenceAnalysisPrompt {
  prompt?: string | null
}

export interface DutySmsConfig {
  inbound_message_prompt?: string | null
}

/**
 * `app_duties.config.texting`. Both fields are settable through the
 * texting-config PATCH endpoint: `is_texting_only` from the "Texting only
 * duty" switch (mirroring "Operator duty") and `handled_by_angelica` from
 * the "Handled by Angelica" switch alongside it, both on the duty's
 * Connection card.
 */
export interface DutyTextingConfig {
  is_texting_only: boolean
  handled_by_angelica: boolean
}

export interface DutyFactsTemplateInfo {
  template?: string | null
}

export interface DutySystemScriptPrompt {
  prompt?: string | null
  prompt_name?: string | null
}

export interface DutySystemScriptPayload {
  separated_scripts: boolean
  outbound: DutySystemScriptPrompt | null
  inbound: DutySystemScriptPrompt | null
}

export type DutySystemScriptPatchArgs = {
  outbound_prompt: string
  inbound_prompt?: string | null
}

export interface DutyVersionDiffEntry {
  path: string
  old: unknown
  new: unknown
  source?: 'publication' | 'reconciliation'
}

/** A snippet edit the duty's Live script already reads. */
export interface DutyPublishSnippetEdit {
  /** The token id as written in the script. */
  snippet_id: string
  name: string
  /** `variant`: this duty's own wording of the snippet changed. */
  kind: 'snippet' | 'variant'
  changed_at: string
  /** Null when the editor is unknown or masked. */
  changed_by_email: string | null
  is_careforce: boolean
}

export interface DutyBranchPromotionPreview {
  source: 'review'
  target: 'live'
  diff: DutyVersionDiffEntry[]
  summary: string[] | null
  snippet_edits: DutyPublishSnippetEdit[]
}

export interface DutyVersion {
  id: string
  created_at: string
  diff: DutyVersionDiffEntry[] | null
  data: Record<string, unknown> | null
  user_id: string | null
  email: string | null
}

export interface Waypoint {
  name: string
  when: string
}

export interface DutyWaypoints {
  waypoints: Waypoint[]
}

export interface DutyVoiceConfig {
  provider: string
  voiceId: string
  model: string | null
  experimentalControls: Record<string, unknown> | null
}

export interface DutyReasoningModelConfig {
  provider: string
  model: string
  max_tokens: number | null
}

export interface DutyReasoningModelCatalogEntry {
  title: string
  providers: string[]
  models: string[]
}

export interface DutyAssistantOverrides {
  voice: DutyVoiceConfig | null
  model: DutyReasoningModelConfig | null
  modelCatalog: DutyReasoningModelCatalogEntry[]
  voiceCatalog?: DutyVoiceCatalogEntry[]
}

export interface SchemaStringChoices {
  values: string[]
  custom: boolean
  supported: boolean
}

export interface DutyVoiceCatalogEntry {
  title: string
  providers: string[]
  voiceId: SchemaStringChoices
  model: SchemaStringChoices
  experimentalControls: boolean
  required: string[]
}

export type DutyAssistantOverridesPatchArgs = {
  voice?: DutyVoiceConfig | null
  model?: DutyReasoningModelConfig | null
}
